import extend from 'extend';

class Storax {
  constructor($window, logger = null, intialize = true) {
    this.$window = $window;
    this.logger = this._validateLogger(logger);

    this.storageTypes = {
      local: {
        name: 'localStorage',
        fn: this._getLocalStorage.bind(this),
      },
      session: {
        name: 'sessionStorage',
        fn: this._getSessionStorage.bind(this),
      },
      temp: {
        name: 'temporary',
        fn: this._getTempStorage.bind(this),
      },
    };

    this._config = {
      name: 'storax',
      key: 'storax',
      storagePriority: [
        this.storageTypes.local,
        this.storageTypes.session,
        this.storageTypes.temp,
      ],
      entries: {
        enabled: false,
        expiration: 3600,
        deleteExpired: true,
        saveWhenDeletingExpired: true,
      },
    };

    this._storage = {};
    this._data = {};

    if (intialize) {
      this._initialize();
    }
  }
  _initialize() {
    this._config = this._validateConfiguration(this._config);
    this._storage = this._getStorage();
    this._data = this._initializeStorageData();
  }
  _getValue() {
    return this._data;
  }
  _setValue(value, save = true) {
    let result;
    if (this._isPromise(value)) {
      result = value.then((realValue) => {
        this._data = this._copy(realValue);
        if (save) {
          this._save();
        }

        return realValue;
      });
    } else {
      this._data = this._copy(value);
      if (save) {
        this._save();
      }

      result = value;
    }

    return result;
  }
  _resetValue(save = true) {
    return this._setValue(this._config.entries.enabled ? {} : this._getInitialValue(), save);
  }
  _getEntry(name) {
    const { entries } = this._config;
    if (!entries.enabled) {
      throw new Error('Entries are not supported for this object');
    }

    let entry = this._data[name];
    if (entry && entries.deleteExpired) {
      ({ entry } = this._deleteExpiredEntries({ entry }, entries.expiration));
      if (!entry && entries.saveWhenDeletingExpired) {
        this._save();
      }
    }

    return entry || null;
  }
  _getEntryValue(name) {
    const entry = this._getEntry(name);
    return entry ? entry.value : null;
  }
  _saveEntry(name, value, save = true) {
    let result;
    if (this._isPromise(value)) {
      result = value.then((realValue) => {
        this._data[name] = {
          time: this._now(),
          value: this._copy(realValue),
        };

        if (save) {
          this._save();
        }

        return realValue;
      });
    } else {
      this._data[name] = {
        time: this._now(),
        value: this._copy(value),
      };

      if (save) {
        this._save();
      }

      result = value;
    }

    return result;
  }
  _deleteEntry(name, save = true) {
    const exists = !!this._data[name];
    if (exists) {
      delete this._data[name];
      if (save) {
        this._save();
      }
    }

    return exists;
  }
  _delete(reset = true) {
    delete this._storage[this._config.key];
    if (reset) {
      this._setValue({}, false);
    }
  }
  _save() {
    this._storage[this._config.key] = JSON.stringify(this._data);
  }
  _configure() {
    return {};
  }
  _getInitialValue() {
    return {};
  }
  _validateLogger(logger) {
    let result;
    if (logger) {
      if (typeof logger.warn !== 'function') {
        throw new Error('The logger must implement a `warn` method');
      }

      result = logger;
    } else {
      result = null;
    }

    return result;
  }
  _validateConfiguration(defaultConfig) {
    const config = extend(true, {}, defaultConfig, this._configure());
    const missing = ['name', 'key'].some((key) => typeof config[key] === 'undefined');
    if (missing) {
      throw new Error(`Missing required configuration setting: ${missing}`);
    }

    return config;
  }
  _getStorage() {
    let previousType;
    let result;
    const found = this._config.storagePriority.some((storageType) => {
      let fallbackFrom = '';
      if (previousType) {
        fallbackFrom = previousType.name;
      }

      previousType = storageType;
      result = storageType.fn(fallbackFrom);
      return !!result;
    });

    if (!found) {
      throw new Error('None of the specified storage types are available');
    }

    return result;
  }
  _initializeStorageData() {
    const { key, entries } = this._config;
    let data = this._storage[key] ? JSON.parse(this._storage[key]) : null;

    if (data && entries.enabled && entries.deleteExpired) {
      data = this._deleteExpiredEntries(data, entries.expiration);
    } else if (!data) {
      data = entries.enabled ? {} : this._getInitialValue();
      this._storage[key] = JSON.stringify(data);
    }

    return data;
  }
  _getLocalStorage(fallbackFrom = '') {
    const storage = this.$window.localStorage;
    if (storage && fallbackFrom) {
      this._warn(`The browser doesn't support ${fallbackFrom}; using localStorage`);
    }

    return storage;
  }
  _getSessionStorage(fallbackFrom = '') {
    const storage = this.$window.sessionStorage;
    if (storage && fallbackFrom) {
      this._warn(`The browser doesn't support ${fallbackFrom}; using sessionStorage`);
    }

    return storage;
  }
  _getTempStorage(fallbackFrom = '') {
    if (fallbackFrom) {
      this._warn(`The browser doesn't support ${fallbackFrom}; using temporary storage`);
    }

    return {};
  }
  _deleteExpiredEntries(entries, expiration) {
    const result = {};
    const now = this._now();
    Object.keys(entries).forEach((name) => {
      const entry = entries[name];
      if ((now - entry.time) < expiration) {
        result[name] = entry;
      }
    });

    return result;
  }
  _warn(message) {
    if (this.logger) {
      this.logger.warn(message);
    } else {
      // eslint-disable-next-line no-console
      console.warn(message);
    }
  }
  _copy(obj) {
    return extend(true, {}, obj);
  }
  _now() {
    const millisecondsInASecond = 1000;
    return Math.floor(Date.now() / millisecondsInASecond);
  }
  _isPromise(obj) {
    return (
      typeof obj === 'object' &&
      typeof obj.then === 'function' &&
      typeof obj.catch === 'function'
    );
  }
}

export default Storax;
